import React, { useState, Fragment, useEffect } from 'react';
import { Drawer, Avatar, Modal, Dropdown, Menu } from 'antd';
import { DownOutlined, LogoutOutlined } from '@ant-design/icons';
import { NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import PrivateImage from 'helpers/PrivateImage';
import { profile_image_type } from 'helpers/commonFunctions';
import { useQueryClient } from 'react-query';
import { REPORT_LINKS } from 'layouts/utils/reportHeader';
import { CONFIG_LINKS } from 'layouts/utils/configHeader';
import { useRevertAlert } from 'hooks/dashboard/dashboard';
import { PAGES, PROFILE_QUERY_KEY, usePermissions } from 'hooks/permissions/permissions';
import Image from 'helpers/Image';
import "./style.css";


const AppHeader = ({ profileData }) => {
    let navigate = useNavigate();
    const queryClient = useQueryClient();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    const [statusId] = useState(0);

    // Nothing is shown until permissions load, so links don't appear and vanish.
    const { can } = usePermissions();
    const reportLinks = REPORT_LINKS.filter((link) => can(link.page));
    const configLinks = CONFIG_LINKS.filter((link) => can(link.page));
    const canDashboard = can(PAGES.DASHBOARD);
    const canUsers = can(PAGES.USER);
    const canAdmin = canUsers || can(PAGES.ROLES);
    const adminPath = canUsers ? "/admin" : "/admin/roles";
    const canProfile = can(PAGES.PROFILE);

    const navMenu = (links) => (
        <Menu
            className="nav-menu"
            selectedKeys={[pathname]}
            items={links.map((link) => ({ key: link.to, label: <Link to={link.to}>{link.label}</Link> }))}
        />
    );

    const { refetch } = useRevertAlert(statusId);

    const showDrawer = () => {
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
    };

    const logout = () => {
        localStorage.removeItem('token');
        queryClient.removeQueries(PROFILE_QUERY_KEY);
        navigate("/login")
    };

    const confirmLogout = () => {
        Modal.confirm({
            className: "logout-confirm",
            centered: true,
            width: 380,
            icon: <span className="logout-confirm-icon"><LogoutOutlined /></span>,
            title: "Log out?",
            content: "You will be returned to the sign-in screen.",
            okText: "Log Out",
            cancelText: "Cancel",
            okButtonProps: { danger: true },
            onOk: logout,
        });
    };

    const displayName = profileData?.data?.firstName
        ? `${profileData?.data?.firstName} ${profileData?.data?.lastName || ""}`.trim()
        : "My account";

    const initials = (displayName.split(" ").filter(Boolean)[0] || "")
        .slice(0, 2)
        .toUpperCase();

    const profileMenu = (
        <Menu
            className="nav-menu profile-menu"
            items={[
                {
                    key: "identity",
                    type: "group",
                    label: (
                        <div className="profile-identity">
                            <span className="profile-identity-name">{displayName}</span>
                            {profileData?.data?.email ? <span className="profile-identity-meta">{profileData?.data?.email}</span> : null}
                            {profileData?.data?.roleName ? <span className="profile-identity-role">{profileData?.data?.roleName}</span> : null}
                        </div>
                    ),
                },
                { type: "divider" },
                ...(canProfile ? [{ key: "/profile", label: <Link to="/profile">My Profile</Link> }] : []),
                { key: "logout", danger: true, label: "Log Out", onClick: confirmLogout },
            ]}
        />
    );

    useEffect(() => {
        if (statusId !== 0 && statusId !== null) {
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusId])

    useEffect(() => {
        if (localStorage.getItem("token") === null) {
            navigate("/login")
        }
    })

    return (
        <Fragment>
            <header className="jr-view-header">
                <div className="web-logo">
                    <NavLink to="/dashboard">
                        <Image name="woh_hup_logo.png" className="logo" />
                    </NavLink>
                </div>
                <div className="web-links">
                    {canDashboard ? <NavLink to="/dashboard"><Image name="one.png" />Dashboard</NavLink> : null}
                    {reportLinks.length === 0 ? null :
                        <Dropdown overlay={navMenu(reportLinks)} trigger={["hover"]}>
                            <span className={`nav-drop${pathname.startsWith("/reports") ? " active" : ""}`}>
                                <Image name="two.png" />Reports<DownOutlined className="nav-drop-caret" />
                            </span>
                        </Dropdown>}
                    {configLinks.length === 0 ? null :
                        <Dropdown overlay={navMenu(configLinks)} trigger={["hover"]}>
                            <span className={`nav-drop${pathname.startsWith("/configuration") ? " active" : ""}`}>
                                <Image name="three.png" />Configuration<DownOutlined className="nav-drop-caret" />
                            </span>
                        </Dropdown>}
                    {canAdmin ? <NavLink to={adminPath}><Image name="four.png" />Admin</NavLink> : null}
                </div>
                <div className="mobile-view-only">
                    <h1 onClick={showDrawer}>Menu</h1>
                    <Drawer className='dra' placement="right" onClose={onClose} open={open}>
                        <ul onClose={onClose}>
                            {canDashboard ? <li><NavLink onClick={onClose} to="/dashboard">Dashboard</NavLink></li> : null}

                            {reportLinks.length === 0 ? null : <>
                                <li className="dra-group">Reports</li>
                                {reportLinks.map((link) => (
                                    <li key={link.to}>
                                        <NavLink onClick={onClose} to={link.to}>{link.label}</NavLink>
                                    </li>
                                ))}
                            </>}

                            {configLinks.length === 0 ? null : <>
                                <li className="dra-group">Configuration</li>
                                {configLinks.map((link) => (
                                    <li key={link.to}>
                                        <NavLink onClick={onClose} to={link.to}>{link.label}</NavLink>
                                    </li>
                                ))}
                            </>}

                            {canAdmin ? <>
                                <li className="dra-group">Admin</li>
                                <li><NavLink onClick={onClose} to={adminPath}>Admin</NavLink></li>
                            </> : null}
                        </ul>
                    </Drawer>
                </div>
                <div className="web-auth">
                    <Dropdown overlay={profileMenu} trigger={["click"]} placement="bottomRight">
                        <span className="profile-trigger">
                            {profileData?.data?.profileImageUrl ? (
                                <PrivateImage
                                    className="avatar-header avatar-header-img"
                                    privateUrl={profileData?.data?.profileImageUrl}
                                    imgType={profile_image_type}
                                    skeleton="avatar-header avatar-header-skeleton"
                                    fallback={<Avatar className="avatar-header">{initials}</Avatar>}
                                />
                            ) : (
                                <Avatar className="avatar-header">{initials}</Avatar>
                            )}
                            <span className="profile-trigger-name">{displayName}</span>
                            <DownOutlined className="nav-drop-caret" />
                        </span>
                    </Dropdown>
                </div>
            </header>
        </Fragment >
    )
}

export default AppHeader

